import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI, paymentAPI } from '@/services/api';

const STORAGE_KEY = 'student-registration-draft';
const REDIRECT_PATH = '/auth/student-register';

const CLASS_OPTIONS = ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'];
const GENDER_OPTIONS = ['Male', 'Female'];
const RELATIONSHIP_OPTIONS = ['Father', 'Mother', 'Guardian', 'Uncle', 'Aunt', 'Other'];
const PAYMENT_BREAKDOWN = [
  { label: 'Registration and tuition deposit', amount: 2200 },
];

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  studentClass: '',
  password: '',
  confirmPassword: '',
  dateOfBirth: '',
  gender: '',
  religion: '',
  stateOfOrigin: '',
  lga: '',
  previousSchool: '',
  parentName: '',
  relationship: '',
  parentPhone: '',
  parentEmail: '',
  address: '',
  notes: '',
};

const loadDraft = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    return {};
  }
};

const formatCurrency = (amount) => `NGN ${amount.toLocaleString()}`;
const totalAmount = PAYMENT_BREAKDOWN.reduce((sum, item) => sum + item.amount, 0);

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const isEmail = (value) => /\S+@\S+\.\S+/.test(value);
const isPhone = (value) => /^\+?[\d\s-]{10,15}$/.test(value.trim());

const buildStepOneErrors = (formData) => {
  const nextErrors = {};

  if (!formData.fullName.trim()) {
    nextErrors.fullName = 'Full name is required';
  } else if (formData.fullName.trim().length < 3) {
    nextErrors.fullName = 'Full name must be at least 3 characters';
  }

  if (!formData.email.trim()) {
    nextErrors.email = 'Email address is required';
  } else if (!isEmail(formData.email.trim())) {
    nextErrors.email = 'Enter a valid email address';
  }

  if (!formData.phone.trim()) {
    nextErrors.phone = 'Phone number is required';
  } else if (!isPhone(formData.phone)) {
    nextErrors.phone = 'Enter a valid phone number';
  }

  if (!formData.studentClass) {
    nextErrors.studentClass = 'Please select a class';
  }

  if (!formData.dateOfBirth) {
    nextErrors.dateOfBirth = 'Date of birth is required';
  }

  if (!formData.gender) {
    nextErrors.gender = 'Please select a gender';
  }

  if (!formData.password) {
    nextErrors.password = 'Password is required';
  } else if (formData.password.length < 6) {
    nextErrors.password = 'Password must be at least 6 characters';
  }

  if (!formData.confirmPassword) {
    nextErrors.confirmPassword = 'Please confirm your password';
  } else if (formData.confirmPassword !== formData.password) {
    nextErrors.confirmPassword = 'Passwords do not match';
  }

  return nextErrors;
};

const buildStepTwoErrors = (formData) => {
  const nextErrors = {};

  if (!formData.parentName.trim()) {
    nextErrors.parentName = 'Parent or guardian name is required';
  }

  if (!formData.relationship) {
    nextErrors.relationship = 'Please select a relationship';
  }

  if (!formData.parentPhone.trim()) {
    nextErrors.parentPhone = 'Parent phone number is required';
  } else if (!isPhone(formData.parentPhone)) {
    nextErrors.parentPhone = 'Enter a valid parent phone number';
  }

  if (formData.parentEmail.trim() && !isEmail(formData.parentEmail.trim())) {
    nextErrors.parentEmail = 'Enter a valid parent email address';
  }

  if (!formData.address.trim()) {
    nextErrors.address = 'Home address is required';
  }

  return nextErrors;
};

const SectionTitle = ({ icon, title, subtitle }) => (
  <div className="mb-8">
    <div className="flex items-center gap-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-primary to-primary-600 text-white shadow-islamic">
        <i className={`fas ${icon}`} />
      </div>
      <div>
        <h2 className="text-2xl font-black text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
    </div>
  </div>
);

const ProgressBar = ({ currentStep, onStepClick }) => {
  const steps = ['Student', 'Guardian', 'Review', 'Payment', 'Done'];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card">
      <div className="relative flex items-start justify-between gap-3">
        <div className="absolute left-0 right-0 top-5 h-1 rounded-full bg-slate-200" />
        <div
          className="absolute left-0 top-5 h-1 rounded-full bg-gradient-to-r from-primary to-primary-600 transition-all duration-300"
          style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
        />

        {steps.map((label, index) => {
          const step = index + 1;
          const isCurrent = step === currentStep;
          const isComplete = step < currentStep;
          const isClickable = step < currentStep;

          return (
            <button
              key={label}
              type="button"
              onClick={() => isClickable && onStepClick(step)}
              className="relative z-10 flex flex-1 flex-col items-center gap-2 bg-transparent"
              disabled={!isClickable}
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-all ${
                  isComplete || isCurrent
                    ? 'bg-gradient-to-r from-primary to-primary-600 text-white shadow-islamic'
                    : 'bg-slate-200 text-slate-500'
                } ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                {isComplete ? <i className="fas fa-check text-xs" /> : step}
              </span>
              <span className={`text-center text-xs font-semibold ${isCurrent || isComplete ? 'text-primary' : 'text-slate-400'}`}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

  const TextField = ({
    label,
    name,
    value,
    onChange,
    placeholder,
    showPassword,
    onTogglePassword,
    type = 'text',
  error,
  required = false,
  textarea = false,
}) => {
  const sharedClass = `w-full rounded-xl border-2 bg-white px-4 py-3.5 text-sm text-slate-800 outline-none transition-all ${
    error
      ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
      : 'border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10'
  }`;

  const isPassword = type === 'password';

  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {textarea ? (
          <textarea
            rows={4}
            className={`${sharedClass} resize-none`}
            value={value}
            onChange={(event) => onChange(name, event.target.value)}
            placeholder={placeholder}
          />
        ) : (
          <input
            type={isPassword && showPassword ? 'text' : type}
            className={sharedClass}
            value={value}
            onChange={(event) => onChange(name, event.target.value)}
            placeholder={placeholder}
          />
        )}
        {isPassword && onTogglePassword && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        )}
      </div>
      {error ? <p className="mt-2 text-xs font-medium text-red-500">{error}</p> : null}
    </div>
  );
};

const SelectField = ({ label, name, value, onChange, options, error, required = false }) => (
  <div>
    <label className="mb-2 block text-sm font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      value={value}
      onChange={(event) => onChange(name, event.target.value)}
      className={`w-full rounded-xl border-2 bg-white px-4 py-3.5 text-sm text-slate-800 outline-none transition-all ${
        error
          ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
          : 'border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10'
      }`}
    >
      <option value="">Select {label}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
    {error ? <p className="mt-2 text-xs font-medium text-red-500">{error}</p> : null}
  </div>
);

const ReviewItem = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-b-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="max-w-[60%] text-right text-sm font-semibold text-slate-800">{value || '-'}</span>
  </div>
);

const StudentRegister = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPasswordReg, setShowPasswordReg] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState(() => ({
    ...EMPTY_FORM,
    ...loadDraft(),
  }));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [paymentState, setPaymentState] = useState('idle');
  const [transactionRef, setTransactionRef] = useState('');
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const transactionId = params.get('transaction_id');
    const transactionRefFromQuery = params.get('transaction_ref');
    const status = params.get('status');

    if (!transactionId && !transactionRefFromQuery && !status) {
      return undefined;
    }

    let isActive = true;

    const verifyPayment = async () => {
      setCurrentStep(4);
      setPaymentState('verifying');
      setStatusMessage('Verifying your payment. Please wait.');

      try {
        const response = await paymentAPI.verify({
          transaction_id: transactionId,
          transaction_ref: transactionRefFromQuery,
          status,
        });

        if (!isActive) {
          return;
        }

        const student = response.data?.data?.student || {};
        const responseMessage = response.data?.message || '';
        const alreadyVerified = responseMessage.toLowerCase().includes('already verified');
        setAdmissionNumber(student.admission_number || '');
        setTransactionRef(transactionRefFromQuery || '');
        setPaymentState('success');
        setStatusMessage(responseMessage || 'Payment verified successfully.');
        setCurrentStep(5);
        sessionStorage.removeItem(STORAGE_KEY);
        window.history.replaceState({}, document.title, window.location.pathname);
        toast.success(alreadyVerified ? 'Registration already completed.' : 'Registration completed successfully.');
      } catch (error) {
        if (!isActive) {
          return;
        }

        const message = getErrorMessage(error, 'Payment verification failed.');
        setPaymentState('failed');
        setStatusMessage(message);
        setCurrentStep(4);
        window.history.replaceState({}, document.title, window.location.pathname);
        toast.error(message);
      }
    };

    verifyPayment();

    return () => {
      isActive = false;
    };
  }, []);

  const updateField = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field]: '',
    }));
  };

  const goToStep = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
    }
  };

  const handleStepOneNext = () => {
    const nextErrors = buildStepOneErrors(formData);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      setCurrentStep(2);
    }
  };

  const handleStepTwoNext = () => {
    const nextErrors = buildStepTwoErrors(formData);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length === 0) {
      setCurrentStep(3);
    }
};

const handleReviewNext = () => {
    const stepOneErrors = buildStepOneErrors(formData);
    if (Object.keys(stepOneErrors).length > 0) {
      setErrors(stepOneErrors);
      setCurrentStep(1);
      return;
    }

    const stepTwoErrors = buildStepTwoErrors(formData);
    if (Object.keys(stepTwoErrors).length > 0) {
      setErrors(stepTwoErrors);
      setCurrentStep(2);
      return;
    }

    setErrors({});
    setCurrentStep(4);
  };

const handlePaymentStart = async () => {
  setSubmitting(true);
  setPaymentState('initiating');
  setStatusMessage('Creating your registration session.');

  try {
    const initResponse = await authAPI.registerInit({
      full_name: formData.fullName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      password: formData.password,
      class: formData.studentClass,
      date_of_birth: formData.dateOfBirth,
      gender: formData.gender,
      religion: formData.religion?.trim() || null,
      state_of_origin: formData.stateOfOrigin?.trim() || null,
      lga: formData.lga?.trim() || null,
      previous_school: formData.previousSchool?.trim() || null,
      parent_name: formData.parentName.trim(),
      parent_phone: formData.parentPhone.trim(),
      parent_email: formData.parentEmail.trim() || null,
      relationship: formData.relationship,
      address: formData.address.trim(),
      notes: formData.notes.trim() || null,
    });

    const nextTransactionRef = initResponse.data?.data?.transaction_ref;

    if (!nextTransactionRef) {
      throw new Error('Transaction reference was not returned by the server.');
    }

    setTransactionRef(nextTransactionRef);
    setStatusMessage('Opening the payment gateway.');

    const paymentResponse = await paymentAPI.initiate({
      transaction_ref: nextTransactionRef,
      redirect_url: `${window.location.origin}${REDIRECT_PATH}`,
    });

    const paymentLink = paymentResponse.data?.data?.payment_link;

    if (!paymentLink) {
      throw new Error('Payment link was not returned by the server.');
    }

    setPaymentState('redirecting');
    toast.success('Redirecting you to the payment gateway.');
    window.location.assign(paymentLink);
  } catch (error) {
    const message = getErrorMessage(error, 'Unable to start payment.');
    setPaymentState('failed');
    setStatusMessage(message);
    toast.error(message);
  } finally {
    setSubmitting(false);
  }
};

  const handleReset = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setFormData(EMPTY_FORM);
    setErrors({});
    setCurrentStep(1);
    setSubmitting(false);
    setPaymentState('idle');
    setTransactionRef('');
    setAdmissionNumber('');
    setStatusMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/40 to-white px-4 py-8">
      <div className="mx-auto grid w-full max-w-7xl gap-8 lg:grid-cols-[1.05fr_1.4fr]">
        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-primary-600 text-white shadow-islamic">
          <div className="flex h-full flex-col justify-between p-8 lg:p-10">
            <div>
              <div className="mb-8 inline-flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                  <i className="fas fa-mosque text-xl" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.24em] text-white/70">Admissions</p>
                  <h1 className="text-xl font-black">Islamic School</h1>
                </div>
              </div>

              <h2 className="max-w-lg text-4xl font-black leading-tight">
                Start your student registration in one place.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-7 text-emerald-50/90">
                Complete your details, review them carefully, and continue to payment. If you return from the payment
                gateway, this page will finish the verification automatically.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-5">
                  <p className="text-sm font-semibold text-white/80">Classes available</p>
                  <p className="mt-2 text-2xl font-black">JSS1 to SS3</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-5">
                  <p className="text-sm font-semibold text-white/80">Current payment</p>
                  <p className="mt-2 text-2xl font-black">{formatCurrency(totalAmount)}</p>
                </div>
              </div>
            </div>

            <div className="mt-10 space-y-5">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-5">
                <div className="flex items-start gap-3">
                  <i className="fas fa-shield-alt mt-1 text-emerald-200" />
                  <div>
                    <p className="font-semibold">Payment and admission flow</p>
                    <p className="mt-1 text-sm leading-6 text-emerald-50/80">
                      Your admission number is created after successful payment verification and will be shown here when
                      you return.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-emerald-50/80">
                <Link to="/auth/student-login" className="font-semibold text-white hover:text-emerald-100">
                  Already registered? Sign in
                </Link>
                <span className="hidden h-1 w-1 rounded-full bg-white/50 sm:block" />
                <Link to="/" className="font-semibold text-white hover:text-emerald-100">
                  Back to home
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-card sm:p-8">
          <ProgressBar currentStep={currentStep} onStepClick={goToStep} />

          <div className="mt-8">
            {currentStep === 1 ? (
              <div>
                <SectionTitle
                  icon="fa-user-graduate"
                  title="Student Details"
                  subtitle="These fields match the account and payment details used by the backend."
                />

                <div className="grid gap-5 md:grid-cols-2">
                  <TextField
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={updateField}
                    placeholder="e.g. Amina Yusuf"
                    required
                    error={errors.fullName}
                  />
                  <TextField
                    label="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={updateField}
                    placeholder="student@example.com"
                    type="email"
                    required
                    error={errors.email}
                  />
                  <TextField
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={updateField}
                    placeholder="e.g. +2348012345678"
                    required
                    error={errors.phone}
                  />
                  <SelectField
                    label="Class"
                    name="studentClass"
                    value={formData.studentClass}
                    onChange={updateField}
                    options={CLASS_OPTIONS}
                    required
                    error={errors.studentClass}
                  />
                  <TextField
                    label="Date of Birth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={updateField}
                    type="date"
                    required
                    error={errors.dateOfBirth}
                  />
                  <SelectField
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={updateField}
                    options={GENDER_OPTIONS}
                    required
                    error={errors.gender}
                  />
                  <TextField
                    label="Religion"
                    name="religion"
                    value={formData.religion}
                    onChange={updateField}
                    placeholder="e.g. Islam, Christianity"
                  />
                  <TextField
                    label="State of Origin"
                    name="stateOfOrigin"
                    value={formData.stateOfOrigin}
                    onChange={updateField}
                    placeholder="Enter your state of origin"
                  />
                  <TextField
                    label="Local Government Area"
                    name="lga"
                    value={formData.lga}
                    onChange={updateField}
                    placeholder="Enter your LGA"
                  />
                  <TextField
                    label="Previous School"
                    name="previousSchool"
                    value={formData.previousSchool}
                    onChange={updateField}
                    placeholder="Enter the school you attended previously"
                  />
                  <TextField
                    label="Password"
                    name="password"
                    value={formData.password}
                    onChange={updateField}
                    placeholder="Create a password"
                    type="password"
                    showPassword={showPasswordReg}
                    onTogglePassword={() => setShowPasswordReg(!showPasswordReg)}
                    required
                    error={errors.password}
                  />
                  <TextField
                    label="Confirm Password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={updateField}
                    placeholder="Repeat your password"
                    type="password"
                    showPassword={showConfirmPassword}
                    onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
                    required
                    error={errors.confirmPassword}
                  />
                </div>

                <div className="mt-8 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={handleStepOneNext}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-primary-600 px-6 py-3 font-semibold text-white shadow-islamic transition hover:opacity-95"
                  >
                    Continue
                    <i className="fas fa-arrow-right text-xs" />
                  </button>
                </div>
              </div>
            ) : null}

            {currentStep === 2 ? (
              <div>
                <SectionTitle
                  icon="fa-users"
                  title="Parent or Guardian"
                  subtitle="We keep an emergency contact and home address for follow-up during admission."
                />

                <div className="grid gap-5 md:grid-cols-2">
                  <TextField
                    label="Parent or Guardian Name"
                    name="parentName"
                    value={formData.parentName}
                    onChange={updateField}
                    placeholder="e.g. Ibrahim Yusuf"
                    required
                    error={errors.parentName}
                  />
                  <SelectField
                    label="Relationship"
                    name="relationship"
                    value={formData.relationship}
                    onChange={updateField}
                    options={RELATIONSHIP_OPTIONS}
                    required
                    error={errors.relationship}
                  />
                  <TextField
                    label="Parent Phone Number"
                    name="parentPhone"
                    value={formData.parentPhone}
                    onChange={updateField}
                    placeholder="e.g. +2348012345678"
                    required
                    error={errors.parentPhone}
                  />
                  <TextField
                    label="Parent Email Address"
                    name="parentEmail"
                    value={formData.parentEmail}
                    onChange={updateField}
                    placeholder="optional@example.com"
                    type="email"
                    error={errors.parentEmail}
                  />
                  <div className="md:col-span-2">
                    <TextField
                      label="Home Address"
                      name="address"
                      value={formData.address}
                      onChange={updateField}
                      placeholder="Enter your full home address"
                      textarea
                      required
                      error={errors.address}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <TextField
                      label="Extra Notes"
                      name="notes"
                      value={formData.notes}
                      onChange={updateField}
                      placeholder="Optional notes for the admission team"
                      textarea
                    />
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <i className="fas fa-arrow-left text-xs" />
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleStepTwoNext}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-primary-600 px-6 py-3 font-semibold text-white shadow-islamic transition hover:opacity-95"
                  >
                    Review details
                    <i className="fas fa-arrow-right text-xs" />
                  </button>
                </div>
              </div>
            ) : null}

            {currentStep === 3 ? (
              <div>
                <SectionTitle
                  icon="fa-clipboard-check"
                  title="Review Application"
                  subtitle="Check the details below before you continue to payment."
                />

                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-black text-slate-900">Student details</h3>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className="text-sm font-semibold text-primary hover:text-primary-600"
                      >
                        Edit
                      </button>
                    </div>
                    <ReviewItem label="Full name" value={formData.fullName} />
                    <ReviewItem label="Email" value={formData.email} />
                    <ReviewItem label="Phone" value={formData.phone} />
                    <ReviewItem label="Class" value={formData.studentClass} />
                    <ReviewItem label="Date of Birth" value={formData.dateOfBirth || 'N/A'} />
                    <ReviewItem label="Gender" value={formData.gender || 'N/A'} />
                    <ReviewItem label="Religion" value={formData.religion || 'N/A'} />
                    <ReviewItem label="State of Origin" value={formData.stateOfOrigin || 'N/A'} />
                    <ReviewItem label="LGA" value={formData.lga || 'N/A'} />
                    <ReviewItem label="Previous School" value={formData.previousSchool || 'N/A'} />
                    <ReviewItem label="Password" value="Hidden for security" />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-black text-slate-900">Guardian details</h3>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(2)}
                        className="text-sm font-semibold text-primary hover:text-primary-600"
                      >
                        Edit
                      </button>
                    </div>
                    <ReviewItem label="Name" value={formData.parentName} />
                    <ReviewItem label="Relationship" value={formData.relationship} />
                    <ReviewItem label="Phone" value={formData.parentPhone} />
                    <ReviewItem label="Email" value={formData.parentEmail || 'Not provided'} />
                    <ReviewItem label="Address" value={formData.address} />
                    <ReviewItem label="Notes" value={formData.notes || 'None'} />
                  </div>
                </div>

                <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/5 p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-primary">
                      <i className="fas fa-receipt" />
                    </div>
                    <div>
                      <h3 className="font-black text-primary">Payment summary</h3>
                      <p className="text-sm text-slate-600">Total due today: {formatCurrency(totalAmount)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <i className="fas fa-arrow-left text-xs" />
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleReviewNext}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-primary-600 px-6 py-3 font-semibold text-white shadow-islamic transition hover:opacity-95"
                  >
                    Continue to payment
                    <i className="fas fa-arrow-right text-xs" />
                  </button>
                </div>
              </div>
            ) : null}

            {currentStep === 4 ? (
              <div>
                <SectionTitle
                  icon="fa-credit-card"
                  title="Payment"
                  subtitle="We will create a registration session first, then send you to the payment gateway."
                />

                <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr]">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                    <h3 className="mb-4 text-lg font-black text-slate-900">Fee breakdown</h3>
                    <div className="space-y-3">
                      {PAYMENT_BREAKDOWN.map((item) => (
                        <div key={item.label} className="flex items-center justify-between border-b border-slate-200 py-3 last:border-b-0">
                          <span className="text-sm text-slate-600">{item.label}</span>
                          <span className="text-sm font-bold text-slate-900">{formatCurrency(item.amount)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between rounded-xl bg-white px-4 py-4">
                      <span className="font-bold text-primary">Total</span>
                      <span className="text-lg font-black text-primary">{formatCurrency(totalAmount)}</span>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-6">
                    <h3 className="mb-4 text-lg font-black text-slate-900">Payment status</h3>

                    <div className="mb-5 rounded-2xl bg-slate-900 p-5 text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Admission checkout</p>
                          <p className="mt-2 text-2xl font-black">{formatCurrency(totalAmount)}</p>
                        </div>
                        <i className="fas fa-shield-alt text-2xl text-emerald-300" />
                      </div>
                      {transactionRef ? (
                        <p className="mt-4 text-xs text-slate-400">Transaction ref: {transactionRef}</p>
                      ) : null}
                    </div>

                    {statusMessage ? (
                      <div
                        className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
                          paymentState === 'failed'
                            ? 'border-red-200 bg-red-50 text-red-600'
                            : 'border-emerald-100 bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {statusMessage}
                      </div>
                    ) : null}

                    {paymentState === 'verifying' ? (
                      <div className="py-10 text-center">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                        <p className="mt-4 font-semibold text-slate-700">Verifying payment...</p>
                      </div>
                    ) : null}

                    {paymentState !== 'verifying' ? (
                      <div className="space-y-4">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                          Once you click the button below, the app will create a registration session and redirect you to
                          the payment gateway.
                        </div>

                        <button
                          type="button"
                          onClick={handlePaymentStart}
                          disabled={submitting}
                          className="inline-flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-primary to-primary-600 px-6 py-4 font-semibold text-white shadow-islamic transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          {submitting ? (
                            <>
                              <i className="fas fa-circle-notch animate-spin" />
                              Preparing payment...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-lock" />
                              Pay {formatCurrency(totalAmount)}
                            </>
                          )}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    disabled={submitting || paymentState === 'verifying'}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className="fas fa-arrow-left text-xs" />
                    Back
                  </button>
                  {paymentState === 'failed' ? (
                    <button
                      type="button"
                      onClick={handleReset}
                      className="inline-flex items-center gap-2 rounded-xl border border-primary/20 px-5 py-3 font-semibold text-primary transition hover:bg-primary/5"
                    >
                      Start again
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {currentStep === 5 ? (
              <div className="py-6 text-center">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <i className="fas fa-check text-4xl" />
                </div>
                <h2 className="mt-6 text-3xl font-black text-slate-900">Registration successful</h2>
                <p className="mx-auto mt-3 max-w-xl text-slate-500">
                  Your payment has been verified and your student account is ready. Keep your admission number safe for
                  login.
                </p>

                <div className="mx-auto mt-8 max-w-md rounded-3xl border border-primary/20 bg-primary/5 p-8">
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary/70">Admission number</p>
                  <p className="mt-3 text-3xl font-black text-primary">{admissionNumber || 'Pending'}</p>
                  <p className="mt-3 text-sm text-slate-600">You can now sign in with this admission number and your password.</p>
                </div>

                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => navigate('/auth/student-login')}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-primary-600 px-6 py-3 font-semibold text-white shadow-islamic transition hover:opacity-95"
                  >
                    <i className="fas fa-sign-in-alt" />
                    Go to student login
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-6 py-3 font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Register another student
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default StudentRegister;
